'use client';

import type { GlobalFilterItem } from '@weberus/shared-types';
import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { InView } from 'react-intersection-observer';
import { BottomSheet, cn, RectangleButton, Tab } from '@yanolja-rnd/nol-design-system';
import { produce } from 'immer';
import { IcRefresh } from '@yanolja-rnd/nol-icons';
import { deviceAtom } from '@/app/_shared/store/device';
import {
  globalFilterAtom,
  globalFilterSelectedAreaKeyAtom,
  isResetButtonEnabledAtom,
  originalGlobalFilterAtom,
  resetGlobalFilterItemsAtom,
} from '@/app/_shared/store/search-filter';
import { FilterRenderer, globalFilterTypeCheckers } from './filter-renderer';
import { useSyncGlobalFilterWithQuery } from './hooks/use-sync-global-filter-with-query';
import useSwiperTabScroll from './hooks/use-swiper-tab-scroll';

interface GlobalFilterModalProps {
  isOpen: boolean;
  closeModal: () => void;
  description?: string;
  countData?: { count?: number | null; text?: string; enabled?: boolean };
  onImpressionFilter: (item: GlobalFilterItem, index: number) => void;
  onClickHistogramHandle: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onFocusHistogramInput: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onMoreButtonClick: (serverLogMeta: GlobalFilterItem['serverLogMeta']) => void;
  onResetButtonClick: () => void;
  onSubmitButtonClick: (resultCount: number | undefined) => void;
  onImpressionNoResult: () => void;
}

const DEFAULT_CTA_BUTTON_TEXT = '적용 하기';

export const GlobalFilterModal: FC<GlobalFilterModalProps> = ({
  isOpen,
  closeModal,
  description,
  countData,
  onImpressionFilter,
  onClickHistogramHandle,
  onFocusHistogramInput,
  onMoreButtonClick,
  onResetButtonClick,
  onSubmitButtonClick,
  onImpressionNoResult,
}) => {
  const [isTop, setIsTop] = useState(true);

  /**
   * 모달의 애니메이션 중 렌더링 안정성을 위한 상태 변수
   * 이 상태가 변경되면 리렌더링이 발생하여 애니메이션 중 중복 이벤트 발생을 방지함
   */
  const [isContentMounted, setIsContentMounted] = useState(false);
  const [isModalReady, setIsModalReady] = useState(false);
  const [searchCountData, setSearchCountData] = useState(countData);
  const bottomSheetBodyRef = useRef<HTMLDivElement>(null);
  const impressedItemsRef = useRef<Set<number>>(new Set());

  // store
  const [globalFilter, setGlobalFilter] = useAtom(globalFilterAtom);

  // store getter
  const { deviceType } = useAtomValue(deviceAtom);
  const isResetButtonEnabled = useAtomValue(isResetButtonEnabledAtom);
  const originalGlobalFilter = useAtomValue(originalGlobalFilterAtom);
  const globalFilterSelectedAreaKey = useAtomValue(globalFilterSelectedAreaKeyAtom);

  // store setter
  const resetGlobalFilterItems = useSetAtom(resetGlobalFilterItemsAtom);

  const hasShortTitle = (item: GlobalFilterItem): item is GlobalFilterItem & { shortTitle: string } => {
    return !!item.shortTitle;
  };

  const hasSelectedItems = useCallback((filterItem: GlobalFilterItem): boolean => {
    if (globalFilterTypeCheckers.isAccordionType(filterItem)) {
      return filterItem.items.some((accordionItem) => accordionItem.items.some((chipItem) => chipItem.selected));
    }
    return filterItem.items.some((chip) => chip.selected);
  }, []);

  const dotBadgeIndexes = globalFilter?.items
    ? globalFilter.items?.map((item, idx) => (hasSelectedItems(item) ? idx + 1 : -1)).filter((idx) => idx !== -1)
    : [];

  const setFilterState = (index: number, newState: GlobalFilterItem): void => {
    const tempVal = produce(globalFilter, (draft) => {
      draft?.items ? (draft.items[index] = newState) : draft;
      return draft;
    });
    setGlobalFilter(tempVal);
  };

  const {
    sectionArrRef,
    fixedNavRef,
    currentSectionIndex,
    setCurrentSectionIndex,
    scrollToSection,
    updateCurrentSectionIndex,
  } = useSwiperTabScroll({ itemLength: globalFilter?.items?.length || 0, rootRef: bottomSheetBodyRef });

  const { syncGlobalFilterWithQuery } = useSyncGlobalFilterWithQuery();

  useEffect(() => {
    if (!isOpen || !globalFilterSelectedAreaKey || !originalGlobalFilter) return;

    // 퀵필터에서 선택한 칩이 있는 경우, 해당 칩이 속한 섹션으로 스크롤
    const selectedAreaIndex = originalGlobalFilter.items.findIndex((item) => {
      if (globalFilterTypeCheckers.isAccordionType(item)) {
        return item.items.some((accordionItem) =>
          accordionItem.items.some((chipItem) => chipItem.key === globalFilterSelectedAreaKey),
        );
      } else if (
        globalFilterTypeCheckers.isChipType(item) ||
        globalFilterTypeCheckers.isCheckboxListType(item) ||
        globalFilterTypeCheckers.isSwitchType(item) ||
        globalFilterTypeCheckers.isHistogramNumberRangeType(item)
      ) {
        return item.items.some((i) => i.key === globalFilterSelectedAreaKey);
      }
      return false;
    });

    if (selectedAreaIndex !== -1) {
      setTimeout(() => {
        selectedAreaIndex && setCurrentSectionIndex(selectedAreaIndex);
        scrollToSection(selectedAreaIndex, 'auto');
      }, 0);
    }
  }, [globalFilterSelectedAreaKey, isOpen]);

  useEffect(() => {
    setSearchCountData(countData);
    if (isOpen && countData?.count !== undefined && countData.count === 0) {
      onImpressionNoResult?.();
    }
  }, [countData]);

  useEffect(() => {
    // 모달이 열릴 때는 즉시 true로 설정
    if (!isOpen) {
      impressedItemsRef.current.clear();
      return;
    }
    setIsContentMounted(true);
    setTimeout(() => setIsModalReady(true), 500);
  }, [isOpen]);

  if (!globalFilter) {
    return;
  }

  return (
    <BottomSheet
      height="full"
      isOpen={isOpen}
      os={deviceType}
      header="필터"
      zIndex={30}
      bottomSheetBodyRef={bottomSheetBodyRef}
      dialogOptions={{
        className: 'w-[600px] max-w-[100vw]',
      }}
      footer={
        <div className="flex gap-8">
          <RectangleButton
            size="large"
            theme="ghost"
            weight="bold"
            disabled={!isResetButtonEnabled}
            icon={<IcRefresh />}
            onClick={() => {
              onResetButtonClick();
              resetGlobalFilterItems();
            }}>
            초기화
          </RectangleButton>
          <RectangleButton
            size="large"
            className="flex-grow"
            weight="bold"
            disabled={searchCountData?.enabled === false}
            onClick={() => {
              onSubmitButtonClick(searchCountData?.count || undefined);
              syncGlobalFilterWithQuery();
              closeModal();
            }}>
            {searchCountData?.text || DEFAULT_CTA_BUTTON_TEXT}
          </RectangleButton>
        </div>
      }
      onCloseEnd={() => {
        // 애니메이션이 종료된 후, 상태를 변경하여 렌더링 안정화
        !isOpen && setIsContentMounted(false);
      }}
      onAnimationEnd={() => {
        // 애니메이션이 종료된 후, 상태를 변경하여 렌더링 안정화
        !isOpen && setIsContentMounted(false);
      }}
      onClose={() => {
        closeModal();
        setGlobalFilter(originalGlobalFilter); // reset global filter
      }}>
      <div
        ref={fixedNavRef}
        className={cn(
          'fixed top-67 z-[1] w-full bg-static-white',
          { 'left-0 right-0 top-63': deviceType === 'etc' },
          { invisible: isTop || !isModalReady },
          'after:absolute after:left-0 after:right-0 after:top-full after:h-0 after:w-full after:shadow-[0_-6px_6px_5px_rgba(0,0,0,0.3)] after:content-[""]',
        )}>
        <Tab.Legacy
          className="bg-static-white"
          dotBadgeIndexes={dotBadgeIndexes}
          selectedIndex={currentSectionIndex}
          setSelectedIndex={setCurrentSectionIndex}
          shapeType="underline"
          tabType="scrollable"
          slidesOffsetBefore={deviceType === 'etc' ? 32 : 16}
          items={globalFilter.items.filter(hasShortTitle).map((item) => item.shortTitle)}
          onTabSelected={(index) => {
            scrollToSection(index);
          }}
        />
      </div>
      <div className="relative mx-auto overflow-hidden">
        <div style={{ height: '0rem', width: '100%' }}>
          <InView
            onChange={(inView) => {
              setIsTop(inView);
            }}>
            <span />
          </InView>
        </div>
        {globalFilter.items.map((item, idx) => (
          <InView
            key={idx}
            root={bottomSheetBodyRef.current}
            rootMargin="-40px 0px 0px 0px"
            onChange={(inView) => {
              if (isOpen && inView && isContentMounted && !impressedItemsRef.current.has(idx)) {
                onImpressionFilter(item, idx);
                impressedItemsRef.current.add(idx);
              }
              updateCurrentSectionIndex(idx, inView);
            }}>
            {({ ref }) => (
              <div
                ref={(el) => el && (sectionArrRef.current[idx] = el)}
                className="relative after:absolute after:bottom-0 after:left-16 after:right-16 after:h-[1px] after:bg-fill-neutral-weak2 last:after:h-0">
                <div ref={ref}>
                  <FilterRenderer
                    filter={item}
                    index={idx}
                    description={description}
                    setFilterState={setFilterState}
                    onClickHistogramHandle={onClickHistogramHandle}
                    onFocusHistogramInput={onFocusHistogramInput}
                    onMoreButtonClick={onMoreButtonClick}
                  />
                </div>
              </div>
            )}
          </InView>
        ))}
      </div>
    </BottomSheet>
  );
};
